export type StaffTag = "BONUS_REDEMPTION" | "DISCOUNT_REQUESTS";

export type PortalRole = "ADMIN" | "STAFF";

export type StaffAccount = {
	id: string;
	email: string;
	fullName: string;
	role: PortalRole;
	tags: StaffTag[];
};

export type StaffSessionResponse = {
	accessToken: string;
	expiresAt: string;
	refreshToken: string;
	refreshExpiresAt: string;
	account: StaffAccount;
};

export type StaffAccessResponse = {
	accessToken: string;
	expiresAt: string;
	account: StaffAccount;
};

export type AuthSession = {
	accessToken: string;
	refreshToken: string;
	expiresAt: string;
	refreshExpiresAt: string;
	accountId: string;
	email: string;
	fullName: string;
	role: PortalRole;
	tags: StaffTag[];
};

export type StaffAuthStoreOptions = {
	storageKey: string;
	persistence: "session" | "local";
};

export type StaffAuthStore = {
	subscribeAuth: (listener: () => void) => () => void;
	getSession: () => AuthSession | null;
	getAccessToken: () => string | null;
	getRefreshToken: () => string | null;
	isPortalAdmin: () => boolean;
	hasStaffTag: (tag: StaffTag) => boolean;
	setSession: (session: AuthSession) => void;
	patchSession: (patch: Partial<AuthSession>) => void;
	clearSession: () => void;
	isAuthenticated: () => boolean;
	loginWithStaffSession: (input: StaffSessionResponse) => void;
	applyStaffAccess: (input: StaffAccessResponse) => void;
	applyStaffAccount: (account: StaffAccount) => void;
};

function persistenceStore(kind: "session" | "local"): Storage | null {
	if (kind === "session") {
		return typeof sessionStorage === "undefined" ? null : sessionStorage;
	}
	return typeof localStorage === "undefined" ? null : localStorage;
}

function parseSession(raw: string | null): AuthSession | null {
	if (!raw) {
		return null;
	}
	try {
		const parsed = JSON.parse(raw) as Partial<AuthSession>;
		if (
			!parsed.accessToken ||
			!parsed.refreshToken ||
			!parsed.email ||
			!parsed.role
		) {
			return null;
		}
		if (parsed.role !== "ADMIN" && parsed.role !== "STAFF") {
			return null;
		}
		return parsed as AuthSession;
	} catch {
		return null;
	}
}

function sessionFromTokens(input: StaffSessionResponse): AuthSession {
	return {
		accessToken: input.accessToken,
		expiresAt: String(input.expiresAt),
		refreshToken: input.refreshToken,
		refreshExpiresAt: String(input.refreshExpiresAt),
		accountId: input.account.id,
		email: input.account.email,
		fullName: input.account.fullName,
		role: input.account.role,
		tags: input.account.tags,
	};
}

export function createStaffAuthStore(
	options: StaffAuthStoreOptions,
): StaffAuthStore {
	const store = () => persistenceStore(options.persistence);
	let memorySession: AuthSession | null = parseSession(
		store()?.getItem(options.storageKey) ?? null,
	);
	const listeners = new Set<() => void>();

	function emitAuthChange(): void {
		for (const listener of listeners) {
			listener();
		}
	}

	function setSession(session: AuthSession): void {
		memorySession = session;
		store()?.setItem(options.storageKey, JSON.stringify(session));
		emitAuthChange();
	}

	function patchSession(patch: Partial<AuthSession>): void {
		if (!memorySession) {
			return;
		}
		setSession({ ...memorySession, ...patch });
	}

	function isPortalAdmin(): boolean {
		return memorySession?.role === "ADMIN";
	}

	return {
		subscribeAuth(listener) {
			listeners.add(listener);
			return () => {
				listeners.delete(listener);
			};
		},
		getSession() {
			return memorySession;
		},
		getAccessToken() {
			return memorySession?.accessToken ?? null;
		},
		getRefreshToken() {
			return memorySession?.refreshToken ?? null;
		},
		isPortalAdmin,
		hasStaffTag(tag) {
			if (isPortalAdmin()) {
				return true;
			}
			return Boolean(memorySession?.tags?.includes(tag));
		},
		setSession,
		patchSession,
		clearSession() {
			memorySession = null;
			store()?.removeItem(options.storageKey);
			emitAuthChange();
		},
		isAuthenticated() {
			return Boolean(memorySession?.accessToken && memorySession?.refreshToken);
		},
		loginWithStaffSession(input) {
			setSession(sessionFromTokens(input));
		},
		applyStaffAccess(input) {
			if (!memorySession) {
				return;
			}
			patchSession({
				accessToken: input.accessToken,
				expiresAt: String(input.expiresAt),
				accountId: input.account.id,
				email: input.account.email,
				fullName: input.account.fullName,
				role: input.account.role,
				tags: input.account.tags,
			});
		},
		applyStaffAccount(account) {
			patchSession({
				accountId: account.id,
				email: account.email,
				fullName: account.fullName,
				role: account.role,
				tags: account.tags,
			});
		},
	};
}
